import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Dashboard from '@mui/icons-material/Dashboard';
import Public from '@mui/icons-material/Public';
import Storage from '@mui/icons-material/Storage';
import { Box } from '@mui/material';
import { FC, ReactElement, useState } from 'react';

import { isProduction } from '../shared/utils';
import MenuItemLink from './MenuItemLink';
import SubMenu from './SubMenu';

const openDocumentation = (url: string) => {
  window.open(url, '_blank');
};

export interface MenuItem {
  id: string;
  name: string;
  icon?: JSX.Element;
  to?: string;
  onClick?: () => void;
  children?: MenuItem[];
}

/**
 * TODO: It is simply static. But in the future it could be dynamically populated via API with based on user permissions
 */
const menuItems: MenuItem[] = [
  {
    id: 'project',
    icon: <Public />,
    name: 'Project',
    children: [
      {
        id: 'project.manage',
        name: 'Manage',
        to: '/projects',
      },
      {
        id: 'project.jobs',
        name: 'Jobs',
        to: '/jobs',
      },
    ],
  },
  {
    id: 'dashboards',
    icon: <Dashboard />,
    name: 'Dashboards',
    children: [
      {
        id: 'dashboard.powerbi',
        name: 'PowerBI',
        onClick: () =>
          openDocumentation(
            'https://app.powerbi.com/groups/me/apps/4841bf8b-74fc-45be-b789-9bd76bbf2139/reports/7d04ba6f-581b-4fe9-a0bc-c5fb0c8ec80f/ReportSection?experience=power-bi',
          ),
      },
      {
        id: 'dashboard.aromaviz',
        name: 'Aroma Viz',
        to: '/data/aroma',
      },
    ],
  },
  {
    id: 'data',
    icon: <Storage />,
    name: 'Datasources',
    children: [
      {
        id: 'Datasources.Datasources',
        name: 'Datasources',
        to: '/data',
      },
    ],
  },
  {
    id: 'pipelines',
    icon: <AccountTreeIcon />,
    name: 'Pipelines',
    children: [
      {
        id: 'Pipelines.Dagster',
        name: 'Dagster',
        onClick: () =>
          openDocumentation(
            isProduction()
              ? 'https://prod.platform.foodpairing.com/dagster/'
              : 'https://staging.platform.foodpairing.com/dagster/',
          ),
      },
    ],
  },
];

interface MenuProps {
  onMenuClick: () => void;
  dense?: boolean;
}

const Menu: FC<MenuProps> = ({ onMenuClick, dense = false }) => {
  const [state, setState] = useState<{
    expandedMenus: { [key: string]: boolean };
  }>({
    expandedMenus: {},
  });

  const open = true;

  const handleToggle = (menuName: string) => {
    if (state.expandedMenus[menuName] === undefined) state.expandedMenus[menuName] = false;
    state.expandedMenus[menuName] = !state.expandedMenus[menuName];
    setState({ expandedMenus: state.expandedMenus });
  };

  const buildMenuItem = (items: MenuItem[], subs?: boolean): ReactElement => (
    <>
      {items.map((item) => {
        if (Array.isArray(item.children) && item.children.length) {
          return (
            <SubMenu
              dense={dense}
              handleToggle={() => handleToggle(item.id)}
              icon={item.icon}
              isOpen={state.expandedMenus[item.id] || false}
              name={item.name || ''}
              sidebarIsOpen={open}
              key={item.name || item.id}
            >
              {buildMenuItem(item.children, true)}
            </SubMenu>
          );
        }

        return (
          <MenuItemLink
            to={item.to || ''}
            primaryText={item.name}
            leftIcon={item.icon}
            onClick={item.onClick ? item.onClick : onMenuClick}
            dense={dense}
            key={item.name || item.id}
            subMenu={subs}
          />
        );
      })}
    </>
  );

  return <Box mt={1}>{buildMenuItem(menuItems)}</Box>;
};

Menu.defaultProps = {
  dense: false,
};

export default Menu;
